/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';

import CartLayout from 'components/RwdBasket/Carts/CartLayout/CartLayout';
import AcceleratedPromotionTerms from 'components/RwdBasket/Carts/AutoreplenishCart/BelowProductPrice/AcceleratedPromotionTerms';
import DeliveryFrequency from 'components/RwdBasket/Carts/AutoreplenishCart/BelowProductPrice/DeliveryFrequency';
import TextZone from 'components/RwdBasket/Carts/AutoreplenishCart/TextZone/TextZone';
import ShippingDeliveryBasicSkuItem from 'components/RwdBasket/Carts/CartLayout/SkuItem/ShippingDeliveryBasicSkuItem';
import ConfirmationBox from 'components/RwdBasket/Carts/CartLayout/ConfirmationBox/ConfirmationBox';
import SaveForLaterConfirmationBox from 'components/RwdBasket/Carts/CartLayout/SaveForLaterConfirmationBox';

import store from 'store/Store';
import { Text, Link } from 'components/ui';
import RwdBasketActions from 'actions/RwdBasketActions/RwdBasketActions';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';
import localeUtils from 'utils/LanguageLocale';
import skuUtils from 'utils/Sku';
import AutoreplenishCartEN from 'components/RwdBasket/Carts/AutoreplenishCart/locales/AutoreplenishCart_en_US';
import AutoreplenishCartFR from 'components/RwdBasket/Carts/AutoreplenishCart/locales/AutoreplenishCart_fr_CA';

import getCallbacks from 'components/RwdBasket/Carts/CartLayout/SkuItem/Buttons/getCallbacks';
import compConstants from 'components/constants';

const AUTO_REPLENISH_BASKET_TYPE = 'autoreplenish';
const allCallbacks = getCallbacks({ isBopis: false, basketType: AUTO_REPLENISH_BASKET_TYPE });
const { getLocalisedDataFromResource } = localeUtils;

function AutoReplenishSkuItem({
    item,
    changeMethodLabel,
    callbacks,
    itemDeliveryMethod,
    preferredStoreInfo,
    preferredZipCode,
    userId,
    hasMetFreeShippingThreshhold,
    isSignedInBIUser,
    isOutOfStock,
    addItemSavingsBasketExperience
}) {
    return (
        <ShippingDeliveryBasicSkuItem
            item={item}
            changeMethodLabel={changeMethodLabel}
            callbacks={callbacks}
            belowPriceContent={[DeliveryFrequency, AcceleratedPromotionTerms]}
            itemDeliveryMethod={itemDeliveryMethod}
            preferredStoreInfo={preferredStoreInfo}
            preferredZipCode={preferredZipCode}
            userId={userId}
            hasMetFreeShippingThreshhold={hasMetFreeShippingThreshhold}
            isSignedInBIUser={isSignedInBIUser}
            isOutOfStock={isOutOfStock}
            addItemSavingsBasketExperience={addItemSavingsBasketExperience}
        />
    );
}

class AutoreplenishCart extends BaseClass {
    constructor(props) {
        super(props);

        this.state = { qtyErrorCommerceIds: [], revertNonce: {}, attemptedQtyByCommerceId: {} };
    }

    handleArQtyUpdate = ({ commerceId, newQty, sku }) => {
        this.setState(prev => ({
            qtyErrorCommerceIds: prev.qtyErrorCommerceIds.filter(id => id !== commerceId)
        }));

        const dispatch = this.props.dispatch || store.dispatch;
        const result = dispatch(
            RwdBasketActions.updateSkuQuantity({
                commerceId,
                newQty,
                sku,
                isBopis: false
            })
        );

        if (result && typeof result.then === 'function') {
            return result.catch(() => {
                this.setState(prev => ({
                    qtyErrorCommerceIds: prev.qtyErrorCommerceIds.includes(commerceId)
                        ? prev.qtyErrorCommerceIds
                        : [...prev.qtyErrorCommerceIds, commerceId],
                    revertNonce: {
                        ...prev.revertNonce,
                        [commerceId]: (prev.revertNonce[commerceId] || 0) + 1
                    },
                    attemptedQtyByCommerceId: {
                        ...prev.attemptedQtyByCommerceId,
                        [commerceId]: newQty
                    }
                }));
            });
        }

        return result;
    };

    skuComponentMapper = (getText, itemErrorMap) => {
        const {
            items,
            itemDeliveryMethod,
            preferredStoreInfo,
            preferredZipCode,
            userId,
            hasMetFreeShippingThreshhold,
            isSignedIn,
            isSignedInBIUser,
            addItemSavingsBasketExperience
        } = this.props;

        const qtyPickerEnabled = isChangeQuantityEnabled();
        const onQtyUpdate = this.props.onQtyUpdate
            ? ({ commerceId, newQty, sku }) => this.props.onQtyUpdate(commerceId, newQty, sku)
            : this.handleArQtyUpdate;

        return items.map((item, index) => {
            item.error = itemErrorMap.get(item.commerceId);
            item.enablePageRenderTracking = index < compConstants.PAGE_RENDER_TRACKING_LIMIT;

            const props = {
                key: item.sku.skuId,
                item: item,
                changeMethodLabel: getText('changeMethod'),
                callbacks: {
                    ...allCallbacks,
                    onQtyUpdate: qtyPickerEnabled ? onQtyUpdate : null
                },
                itemDeliveryMethod,
                preferredStoreInfo,
                preferredZipCode,
                userId,
                hasMetFreeShippingThreshhold,
                isSignedIn,
                isSignedInBIUser,
                isOutOfStock: item.sku.isOutOfStock,
                addItemSavingsBasketExperience
            };

            if (skuUtils.isStandardProduct(item.sku)) {
                const hasQtyError = this.state.qtyErrorCommerceIds.includes(item.commerceId);
                const lineKey = `${item.commerceId}-${this.state.revertNonce[item.commerceId] || 0}`;
                const retryQty = this.state.attemptedQtyByCommerceId[item.commerceId] ?? item.qty;

                return (
                    <React.Fragment key={lineKey}>
                        <AutoReplenishSkuItem {...props} />
                        {hasQtyError && (
                            <Text
                                is='p'
                                color='error'
                                fontSize='sm'
                                marginTop={2}
                            >
                                {getText('qtyUpdateError')}{' '}
                                <Link
                                    color='blue'
                                    onClick={() => onQtyUpdate({ commerceId: item.commerceId, newQty: retryQty, sku: item.sku })}
                                    children={getText('retry')}
                                />
                            </Text>
                        )}
                    </React.Fragment>
                );
            }

            throw new Error(`Shipping and Delivery sku mapper received sku of type: ${item.sku.type}`);
        });
    };

    showHeadlineError = () => {
        const { items, hasErrorZone1Message } = this.props;
        const hasOOSItems = item => item.sku.isOutOfStock;

        return items.some(hasOOSItems) && !hasErrorZone1Message;
    };

    handleDismissBasketConfirmation = () => {
        this.props.clearBasketConfirmation();
    };

    handleUndoSaveForLater = () => {
        const { basketConfirmation, userId, listId, undoAddToSaveForLater } = this.props;

        if (basketConfirmation) {
            undoAddToSaveForLater({
                ...basketConfirmation,
                userId,
                listId
            });
        }
    };

    handleOpenModal = () => {
        this.props.showSavedForLaterModal({ isOpen: true });
    };

    render() {
        const {
            totalQuantity,
            isSignedIn,
            totalAnnualReplenishmentDiscount,
            firstName,
            hasAcceleratedPromotionItems,
            errors,
            itemErrorMap,
            confirmationBoxInfo,
            infoModalCallbacks,
            basketConfirmation
        } = this.props;

        const getText = getLocalisedDataFromResource(AutoreplenishCartEN, AutoreplenishCartFR);

        return (
            <CartLayout
                title={getText('autoReplenishTitle', [totalQuantity])}
                infoModalCallbacks={infoModalCallbacks}
                iconName={this.showHeadlineError() ? 'alert' : 'autoReplenish'}
                iconColor={this.showHeadlineError() ? 'red' : null}
                confirmationBox={
                    <>
                        <ConfirmationBox {...confirmationBoxInfo} />
                        <SaveForLaterConfirmationBox
                            basketConfirmation={basketConfirmation}
                            basketType={AUTO_REPLENISH_BASKET_TYPE}
                            onDismiss={this.handleDismissBasketConfirmation}
                            onUndo={this.handleUndoSaveForLater}
                            onOpenModal={this.handleOpenModal}
                        />
                    </>
                }
                skuItemComponents={this.skuComponentMapper(getText, itemErrorMap)}
                errors={errors}
                textZone={
                    totalAnnualReplenishmentDiscount && (
                        <TextZone
                            isSignedIn={isSignedIn}
                            totalAnnualReplenishmentDiscount={totalAnnualReplenishmentDiscount}
                            firstName={firstName}
                            hasAcceleratedPromotionItems={hasAcceleratedPromotionItems}
                        />
                    )
                }
            />
        );
    }
}

export default wrapComponent(AutoreplenishCart, 'AutoreplenishCart', true);
